<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';

import DetailedJobInfoDescListItem from '@/components/items/DetailedJobInfoDescListItem.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import * as commonConstants from '@/constants/common';

export interface Props {
  job?: ReadonlyDeep<
    Partial<
      Pick<
        openapi.AirroadJobDetailedContentOpenapi,
        | 'customer'
        | 'status'
        | 'booked_by'
        | 'booked_at'
        | 'reference_1'
        | 'reference_2'
        | 'reference_3'
      >
    >
  >;
  withoutReferences?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  job: undefined,
  withoutReferences: false,
});

const timeZone = 'Australia/Sydney';

function formatDate(date: string, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d'); // 04:50 PM, Sep 24
}

const job = computed(() => props.job || {});
</script>

<template>
  <dl
    :class="$style.legChooseNewAirRoadStepAdditionalInfoDescList"
    data-testid="legChooseNewAirRoadStepAdditionalInfoDescList"
  >
    <div :class="$style.wrapper">
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Customer">
          <template v-if="job.customer">
            {{ job.customer }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Status">
          <template v-if="job.status">
            {{ job.status }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Booked by">
          <template v-if="job.booked_by">
            {{ job.booked_by }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Booked at">
          <template v-if="job.booked_at">
            <span>
              <KParentTooltip>
                {{ job.booked_at }}
              </KParentTooltip>
              {{ formatDate(job.booked_at, timeZone) }}
            </span>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div v-if="!props.withoutReferences" :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Reference 1">
          <template v-if="job.reference_1">
            {{ job.reference_1 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div v-if="!props.withoutReferences" :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Reference 2">
          <template v-if="job.reference_2">
            {{ job.reference_2 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div v-if="!props.withoutReferences" :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Reference 3">
          <template v-if="job.reference_3">
            {{ job.reference_3 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
    </div>
  </dl>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.legChooseNewAirRoadStepAdditionalInfoDescList {
  line-height: 1.4;
  font-size: 16px;
  margin: 0;
}
  .wrapper {
    margin: 0 -1rem -1rem -1rem;
    display: flex;
    justify-content: space-between;
  }
    .itemWrapper {
      display: inline-block;
      margin: 0 1rem;
      min-width: 90px;
    }
      .item {
        display: inline-block;
        margin: 0 0 1rem 0;
      }
</style>
